import * as React from 'react';
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import Button from '@mui/material/Button';
import AccountCircle from '@mui/icons-material/AccountCircle';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuList from '@mui/material/MenuList';
import Grow from '@mui/material/Grow';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { isUserAuthenticated } from './utils/authService';
import 'primeicons/primeicons.css';

// Import your blog functionality
import { getAllBlogs } from './blogs/index';

// Styled components for search bar
const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(3),
        width: 'auto',
    },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: '20ch',
        },
    },
}));

// Blog Dropdown Component
const BlogDropdown = () => {
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef(null);
    const navigate = useNavigate();

    // Load blogs
    const blogs = React.useMemo(() => {
        try {
            return getAllBlogs();
        } catch (error) {
            console.warn("Error loading blogs for navbar:", error);
            return [];
        }
    }, []);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (event && anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    // Group blogs by categories
    const groupedBlogs = React.useMemo(() => {
        const categories = [
            { name: "Recent Posts", items: [] },
            { name: "Popular Guides", items: [] },
            { name: "Walmart Strategies", items: [] },
        ];

        if (blogs.length > 0) {
            categories[0].items = blogs.slice(0, 5);
            const remainingCount = blogs.length - 5;
            const halfRemaining = Math.floor(remainingCount / 2);
            categories[1].items = blogs.slice(5, 5 + halfRemaining);
            categories[2].items = blogs.slice(5 + halfRemaining);
        }

        return categories;
    }, [blogs]);

    const handleBlogClick = (url) => {
        handleClose();
        navigate(url);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            <Button
                ref={anchorRef}
                onClick={handleToggle}
                endIcon={<KeyboardArrowDownIcon />}
                color="inherit"
                sx={{
                    fontWeight: 500,
                    '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                    }
                }}
            >
                Blog
            </Button>
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="bottom-start"
                transition
                disablePortal
                style={{ zIndex: 1500 }}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                        }}
                    >
                        <Paper
                            elevation={3}
                            sx={{
                                mt: 1,
                                maxHeight: '80vh',
                                overflowY: 'auto',
                                width: '900px',
                                borderRadius: '12px'
                            }}
                        >
                            <ClickAwayListener onClickAway={handleClose}>
                                <Box>
                                    <Grid container sx={{ p: 2 }}>
                                        {groupedBlogs.map((category, catIndex) => (
                                            <Grid item xs={6} key={catIndex} sx={{ p: 1 }}>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: '#2ECC40',
                                                        borderBottom: '2px solid #2ECC40',
                                                        pb: 1,
                                                        mb: 2
                                                    }}
                                                >
                                                    {category.name}
                                                </Typography>

                                                <MenuList sx={{ p: 0 }}>
                                                    {category.items.map((item, index) => (
                                                        <MenuItem
                                                            key={index}
                                                            onClick={() => handleBlogClick(item.url)}
                                                            sx={{
                                                                py: 1,
                                                                px: 1.5,
                                                                borderRadius: '8px',
                                                                mb: 0.5,
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(46, 204, 64, 0.08)'
                                                                }
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 500,
                                                                    fontSize: '0.9rem',
                                                                    color: '#1a1a1a',
                                                                    textAlign: 'left',
                                                                    lineHeight: 1.4
                                                                }}
                                                            >
                                                                {item.title}
                                                            </Typography>
                                                        </MenuItem>
                                                    ))}
                                                </MenuList>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    <Box sx={{ 
                                        px: 3, 
                                        py: 2, 
                                        borderTop: '1px solid rgba(0,0,0,0.08)', 
                                        backgroundColor: 'rgba(0,0,0,0.02)' 
                                    }}>
                                        <Button
                                            onClick={() => handleBlogClick('/blogs')}
                                            variant="contained"
                                            sx={{
                                                fontWeight: 700,
                                                backgroundColor: '#2ECC40',
                                                color: 'white',
                                                borderRadius: '8px',
                                                '&:hover': {
                                                    backgroundColor: '#28a745'
                                                }
                                            }}
                                        >
                                            View All Articles
                                        </Button>
                                    </Box>
                                </Box>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
};

export default function Navbar() {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const loggedIn = isUserAuthenticated();

    // Load blogs for mobile menu
    const blogs = React.useMemo(() => {
        try {
            return getAllBlogs();
        } catch (error) {
            console.warn("Error loading blogs for mobile menu:", error);
            return [];
        }
    }, []);

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMobileMenuOpen(false);
    };

    const handleMobileMenuToggle = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const scrollToSection = (sectionId) => {
        const isHomePage = location.pathname === '/';
        
        if (!isHomePage) {
            // If not on home page, navigate to home page first
            navigate('/');
            
            // Then scroll to the section after a brief delay
            setTimeout(() => {
                const sectionElement = document.getElementById(sectionId);
                if (sectionElement) {
                    const offset = 128;
                    const elementPosition = sectionElement.getBoundingClientRect().top + window.pageYOffset;
                    const offsetPosition = elementPosition - offset;
                    
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth',
                    });
                }
            }, 100);
        } else {
            // Already on home page, just scroll
            const sectionElement = document.getElementById(sectionId);
            if (sectionElement) {
                const offset = 128;
                const elementPosition = sectionElement.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - offset;
                
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                });
            }
        }
        
        // Close mobile menu if open
        setMobileMenuOpen(false);
    };

    const handleLogoClick = () => {
        navigate('/');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const queryParams = new URLSearchParams(window.location.search);
    const extN = queryParams.get("extN");

    if (extN) {
        return null;
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <AppBar position="static" style={{ backgroundColor: "#2ECC40" }}>
                <Toolbar>
                    {/* Logo */}
                    <Typography variant="h6" sx={{ flexGrow: 1 }} onClick={handleLogoClick} style={{ cursor: "pointer" }}>
                        <img src="/histomart_white.png" style={{ width: "15%" }} className="navLogo" />
                    </Typography>

                    {/* Mobile Menu Icon */}
                    <IconButton
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        sx={{ display: { xs: 'block', sm: 'none' } }}
                        onClick={handleMobileMenuToggle}
                    >
                        <MenuIcon />
                    </IconButton>

                    {/* Desktop Navigation */}
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
                        {/* Social Media Icons */}
                        <IconButton
                            size="medium"
                            onClick={() => window.open("https://www.facebook.com/share/g/yhAdje1Da4dQFR2A/", "_blank")}
                            color="inherit"
                            sx={{
                                backgroundColor: 'white',
                                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                                transition: "all 0.3s ease",
                                '&:hover': {
                                    backgroundColor: "rgb(8, 102, 255)",
                                    transform: "scale(1.05)",
                                    boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.3)",
                                    '& .pi': {
                                        color: 'white',
                                    }
                                },
                                '& .pi': {
                                    color: "rgb(8, 102, 255)",
                                    fontSize: 20,
                                }
                            }}
                        >
                            <i className="pi pi-facebook" />
                        </IconButton>

                        <IconButton
                            size="medium"
                            onClick={() => window.open("https://discord.gg/9VfPNDyM9h", "_blank")}
                            color="inherit"
                            sx={{
                                backgroundColor: 'white',
                                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                                transition: "all 0.3s ease",
                                '&:hover': {
                                    backgroundColor: "rgb(114,137,218)",
                                    transform: "scale(1.05)",
                                    boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.3)",
                                    '& .pi': {
                                        color: 'white',
                                    }
                                },
                                '& .pi': {
                                    color: "rgb(114,137,218)",
                                    fontSize: 20,
                                },
                                marginLeft: '10px'
                            }}
                        >
                            <i className="pi pi-discord" />
                        </IconButton>

                        <Divider orientation="vertical" flexItem sx={{ mx: 1, backgroundColor: 'rgba(255,255,255,0.3)' }} />

                        {/* Navigation Links */}
                        <Button color="inherit" onClick={() => navigate('/pricing')}>
                            Pricing
                        </Button>

                        {/* Blog Dropdown */}
                        <BlogDropdown />

                        <Button color="inherit" onClick={() => window.open("https://chromewebstore.google.com/detail/histomart/eocaegpgmbgncbmhmigamhlangcipkco")}>
                            Chrome Extension
                        </Button>

                        {loggedIn && (
                            <Button color="inherit" onClick={() => { window.location.href = "https://www.marteroa.com/" }}>
                                W2W Flips
                            </Button>
                        )}

                        {loggedIn && (
                            <Button 
                                onClick={() => navigate('/search')} 
                                variant="outlined" 
                                color="inherit" 
                                startIcon={<SearchIcon />}
                                sx={{
                                    borderColor: 'rgba(255,255,255,0.5)',
                                    '&:hover': {
                                        borderColor: 'white',
                                        backgroundColor: 'rgba(255,255,255,0.1)'
                                    }
                                }}
                            >
                                Search
                            </Button>
                        )}
                    </Box>

                    {/* Login/Logout Button */}
                    <IconButton
                        size={loggedIn ? "large" : "small"}
                        edge="end"
                        aria-label="account of current user"
                        color="inherit"
                        onClick={() => navigate('/user')}
                        style={{
                            border: !loggedIn && "3px solid darkgreen",
                            padding: "10px",
                            borderRadius: !loggedIn && "5px",
                            marginLeft: "10px"
                        }}
                    >
                        {loggedIn ? <AccountCircle /> : "Login"}
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* Mobile Menu */}
            <Menu
                anchorEl={anchorEl}
                open={mobileMenuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        maxHeight: '80vh',
                        width: '80vw',
                        overflowY: 'auto'
                    }
                }}
            >
                <MenuItem onClick={() => { navigate('/pricing'); handleMenuClose(); }}>
                    Pricing
                </MenuItem>

                {/* Mobile Blog Section */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, my: 1.5, ml: 2, color: '#2ECC40' }}>
                    Blog Articles
                </Typography>

                <Box sx={{ maxHeight: '40vh', overflowY: 'auto', mb: 2 }}>
                    {blogs.slice(0, 15).map((blog, index) => (
                        <MenuItem
                            key={index}
                            onClick={() => {
                                navigate(blog.url);
                                handleMenuClose();
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            sx={{
                                py: 1,
                                pl: 3,
                                fontSize: '0.9rem',
                                textAlign: 'left',
                                borderRadius: '6px',
                                my: 0.5
                            }}
                        >
                            {blog.title}
                        </MenuItem>
                    ))}
                    <MenuItem
                        onClick={() => {
                            navigate('/blogs');
                            handleMenuClose();
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        sx={{
                            fontWeight: 600,
                            color: '#2ECC40',
                            textAlign: 'left',
                            mt: 1
                        }}
                    >
                        View All Blogs →
                    </MenuItem>
                </Box>

                <Divider sx={{ my: 1 }} />

                <MenuItem onClick={() => { 
                    window.open("https://chromewebstore.google.com/detail/histomart/eocaegpgmbgncbmhmigamhlangcipkco");
                    handleMenuClose();
                }}>
                    Chrome Extension
                </MenuItem>

                {loggedIn && (
                    <MenuItem onClick={() => { 
                        window.location.href = "https://www.marteroa.com/";
                        handleMenuClose();
                    }}>
                        W2W Flips
                    </MenuItem>
                )}

                {loggedIn && (
                    <MenuItem onClick={() => { 
                        navigate('/search');
                        handleMenuClose();
                    }}>
                        <Button variant="outlined" color="inherit" startIcon={<SearchIcon />}>
                            Search
                        </Button>
                    </MenuItem>
                )}
            </Menu>

            {/* Hidden SEO links for blog crawling */}
            <Box
                sx={{
                    display: 'none',
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    overflow: 'hidden'
                }}
                aria-hidden="true"
            >
                {blogs.map((blog, index) => (
                    <Link key={index} to={blog.url} title={blog.title}>
                        {blog.title}
                    </Link>
                ))}
            </Box>
        </Box>
    );
}